import { useCallback, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  Buildings, ArrowClockwise, Wallet, Eye, CreditCard, Gift, DeviceMobile, Lightning,
  ToggleRight, ToggleLeft, UsersThree, FilePdf, FileXls,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

// Route slug → vendor_type mapping
const SLUG_TO_TYPE = {
  'card': 'card',
  'gift-card': 'gift_card',
  'recharge': 'recharge',
  'electricity': 'electricity',
};

const CAT_META = {
  card:        { label: 'Card Vendor Ledger',        icon: CreditCard,  color: 'indigo'  },
  gift_card:   { label: 'Gift Card Vendor Ledger',   icon: Gift,        color: 'purple'  },
  recharge:    { label: 'Recharge Vendor Ledger',    icon: DeviceMobile, color: 'sky'    },
  electricity: { label: 'Electricity Vendor Ledger', icon: Lightning,   color: 'amber'   },
};

const ALL_CATS = [
  { key: 'card',        label: 'Card Vendors',        cls: 'border-indigo-600 text-indigo-700 bg-indigo-50' },
  { key: 'gift_card',   label: 'Gift Card Vendors',   cls: 'border-purple-600 text-purple-700 bg-purple-50' },
  { key: 'recharge',    label: 'Recharge Vendors',    cls: 'border-sky-600 text-sky-700 bg-sky-50' },
  { key: 'electricity', label: 'Electricity Vendors', cls: 'border-amber-600 text-amber-700 bg-amber-50' },
  { key: 'all',         label: 'All Vendors',         cls: 'border-fuchsia-600 text-fuchsia-700 bg-fuchsia-50' },
];

function AccessDialog({ open, onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/vendor-ledger-access/');
      setList(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = async (uid, next) => {
    try {
      await api.patch(`/vendor-ledger-access/${uid}`, { enabled: next });
      toast.success(next ? '✅ Access enabled' : '🚫 Access disabled');
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Toggle failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="vl-access-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UsersThree size={20} /> Manage Vendor Ledger Access</DialogTitle></DialogHeader>
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 mb-2">
          💡 Sirf enabled assistants ko ye 4 vendor ledger pages dikhenge. Default OFF. Admin & CA ko hamesha access milta hai.
        </div>
        {loading ? <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
        : list.length === 0 ? <div className="p-4 text-center text-sm text-gray-500">Koi assistant nahi mila</div>
        : (
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {list.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between border-b py-2 px-1" data-testid={`vl-access-row-${u.user_id}`}>
                <div>
                  <div className="font-medium text-sm">{u.name}</div>
                  <div className="text-[11px] text-gray-500">{u.email}</div>
                  {u.set_by_name && <div className="text-[10px] text-gray-400 mt-0.5">Last set: {u.set_by_name}</div>}
                </div>
                <button
                  onClick={() => toggle(u.user_id, !u.enabled)}
                  className="flex items-center gap-1.5"
                  data-testid={`vl-access-toggle-${u.user_id}`}
                >
                  {u.enabled
                    ? <><ToggleRight size={28} className="text-emerald-600" /><span className="text-xs text-emerald-700 font-medium">enabled</span></>
                    : <><ToggleLeft size={28} className="text-gray-400" /><span className="text-xs text-gray-500 font-medium">disabled</span></>}
                </button>
              </div>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function VendorLedgersPage() {
  const { typeSlug } = useParams();
  const { isAccountant, isAdminOrAbove } = useAuth();
  const routeType = typeSlug ? SLUG_TO_TYPE[typeSlug] : null;
  const isSingleType = !!routeType;

  const [meta, setMeta] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState({ entries: [], balance: null });
  const [accounts, setAccounts] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState(routeType || 'card');
  const [showAccess, setShowAccess] = useState(false);

  // Sync route → active category
  useEffect(() => {
    if (routeType) setActiveCategory(routeType);
  }, [routeType]);

  // Access check
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/vendor-ledger-access/me');
        setMeta(r.data);
      } catch { setMeta({ can_access: false }); }
    })();
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/vendors/');
      setVendors(r.data || []);
    } catch (err) {
      if (err?.response?.status !== 403) toast.error('Load failed');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (meta?.can_access) loadList(); }, [meta?.can_access, loadList]);
  useEffect(() => { setSelected(null); }, [activeCategory]);

  const openVendor = async (v) => {
    setSelected(v);
    setDetailLoading(true);
    try {
      const [l, a] = await Promise.all([
        api.get(`/vendors/${v.id}/ledger`),
        api.get(`/vendors/${v.id}/saved-accounts`).catch(() => ({ data: [] })),
      ]);
      setLedger(l.data);
      setAccounts(a.data || []);
    } catch (err) { toast.error('Detail load failed'); }
    finally { setDetailLoading(false); }
  };

  const downloadExcel = async (v) => {
    try {
      const res = await api.get(`/vendors/${v.id}/ledger/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `ledger_${v.name}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Excel download failed'); }
  };

  const downloadPdf = async (v) => {
    try {
      const res = await api.get(`/vendors/${v.id}/ledger/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `ledger_${v.name}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('PDF download failed'); }
  };

  const filtered = useMemo(() => {
    if (activeCategory === 'all') return vendors;
    return vendors.filter((v) => v.vendor_type === activeCategory);
  }, [vendors, activeCategory]);

  const statsFor = (cat) => {
    const list = cat === 'all' ? vendors : vendors.filter((v) => v.vendor_type === cat);
    return list.reduce((acc, v) => ({
      count: acc.count + 1,
      credit: acc.credit + (v.credit || 0),
      debit: acc.debit + (v.debit || 0),
      balance: acc.balance + (v.balance || 0),
      cards: acc.cards + (v.total_cards_uploaded || 0),
    }), { count: 0, credit: 0, debit: 0, balance: 0, cards: 0 });
  };
  const activeStats = statsFor(activeCategory);
  const activeCatMeta = CAT_META[activeCategory] || CAT_META.card;
  const Icon = activeCatMeta.icon;
  const pageTitle = isSingleType ? activeCatMeta.label : 'Vendor Card Ledger';

  // Access gate
  if (!meta) return <div className="p-8 text-center text-sm text-gray-500">Loading...</div>;
  if (!meta.can_access) {
    return (
      <div className="max-w-md mx-auto mt-12 p-6 bg-red-50 border border-red-200 rounded-lg text-center" data-testid="vl-no-access">
        <Wallet size={32} weight="duotone" className="mx-auto text-red-500 mb-2" />
        <div className="font-semibold text-red-900">Access denied</div>
        <p className="text-sm text-red-700 mt-1">Vendor Ledger module ka access nahi hai aapko. Admin se enable karwao.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="vendor-ledgers-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Icon size={26} weight="duotone" className={`text-${activeCatMeta.color}-600`} /> {pageTitle}
          {isAccountant && !isAdminOrAbove && (
            <span className="text-xs font-normal bg-amber-100 text-amber-700 px-2 py-0.5 rounded ml-2">CA Read-Only</span>
          )}
        </h1>
        <div className="ml-auto flex gap-2">
          {meta.can_manage_access && (
            <Button size="sm" variant="outline" onClick={() => setShowAccess(true)} data-testid="vl-access-btn">
              <UsersThree size={14} /> Manage Access
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => { loadList(); setSelected(null); }} data-testid="refresh-btn">
            <ArrowClockwise size={14} /> Refresh
          </Button>
        </div>
      </div>

      {!selected ? (
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="vendor-list">
          {/* Category tabs — only shown when not on single-type route */}
          {!isSingleType && (
            <div className="flex items-center gap-1 border-b border-gray-200 px-3 pt-3 pb-0 overflow-x-auto">
              {ALL_CATS.map((c) => {
                const s = statsFor(c.key);
                const isActive = activeCategory === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setActiveCategory(c.key)}
                    data-testid={`vendor-ledger-cat-tab-${c.key}`}
                    className={`px-3 py-2 text-xs font-semibold whitespace-nowrap rounded-t-md border-b-2 transition ${
                      isActive ? c.cls : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {c.label} <span className="ml-1 text-[10px] font-normal opacity-70">({s.count})</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Category summary cards */}
          <div className="px-4 pt-4 pb-2 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid={`vendor-ledger-stats-${activeCategory}`}>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Category</div>
              <div className="text-sm font-bold text-gray-900 mt-0.5">{activeCatMeta.label}</div>
              <div className="text-[10px] text-gray-500">{activeStats.count} vendors</div>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">Total Cards</div>
              <div className="text-lg font-bold text-indigo-900 mt-0.5">{activeStats.cards}</div>
              <div className="text-[10px] text-indigo-600">uploaded / issued</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Total Credited</div>
              <div className="text-lg font-bold text-emerald-900 mt-0.5">{fmt(activeStats.credit)}</div>
              <div className="text-[10px] text-emerald-600">Cards diye (Cr)</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wide text-red-700 font-semibold">Total Paid Out</div>
              <div className="text-lg font-bold text-red-900 mt-0.5">{fmt(activeStats.debit)}</div>
              <div className="text-[10px] text-red-600">Payment hua (Dr)</div>
            </div>
            <div className={`${activeStats.balance >= 0 ? 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900' : 'bg-amber-50 border-amber-200 text-amber-900'} border rounded-lg p-3`}>
              <div className="text-[10px] uppercase tracking-wide opacity-70 font-semibold">Net Balance (owed)</div>
              <div className="text-lg font-bold mt-0.5">{fmt(activeStats.balance)}</div>
              <div className="text-[10px] opacity-70">Cr − Dr</div>
            </div>
          </div>

          {loading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
          : filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-gray-500">
              <Buildings size={40} weight="duotone" className="mx-auto text-gray-300 mb-2" />
              Is category me abhi koi vendor nahi hai.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Vendor</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-right">Cards</th>
                    <th className="px-3 py-2 text-right">Credited</th>
                    <th className="px-3 py-2 text-right">Paid Out</th>
                    <th className="px-3 py-2 text-right">Balance (owed)</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((v) => (
                    <tr key={v.id} className="border-t hover:bg-gray-50" data-testid={`vendor-row-${v.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold">{v.name}</div>
                        <div className="text-[11px] text-gray-500">{v.email}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{(v.vendor_type || v.role || '').replace('_', ' ')}</td>
                      <td className="px-3 py-2 text-right">{v.total_cards_uploaded || 0}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{fmt(v.credit)}</td>
                      <td className="px-3 py-2 text-right text-red-700">{fmt(v.debit)}</td>
                      <td className="px-3 py-2 text-right font-bold text-fuchsia-700">{fmt(v.balance)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => openVendor(v)} data-testid={`open-vendor-${v.id}`}>
                            <Eye size={12} /> View
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => downloadExcel(v)} title="Download Excel" data-testid={`dl-xls-${v.id}`}>
                            <FileXls size={12} />
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 gap-1 text-red-700 border-red-200 hover:bg-red-50" onClick={() => downloadPdf(v)} title="Download PDF" data-testid={`dl-pdf-${v.id}`}>
                            <FilePdf size={12} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <>
          <Button variant="outline" size="sm" onClick={() => setSelected(null)} data-testid="back-btn">← Back to List</Button>

          {/* Header */}
          <div className="bg-white border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Buildings size={22} className="text-blue-600" />
              <div>
                <div className="text-lg font-bold">{selected.name}</div>
                <div className="text-xs text-gray-500">{selected.email} • {selected.vendor_type || selected.role}</div>
              </div>
              <div className="ml-auto flex gap-1">
                <Button size="sm" variant="outline" className="gap-1 text-emerald-700" onClick={() => downloadExcel(selected)}>
                  <FileXls size={14} /> Excel
                </Button>
                <Button size="sm" variant="outline" className="gap-1 text-red-700" onClick={() => downloadPdf(selected)}>
                  <FilePdf size={14} /> PDF
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                <div className="text-[10px] uppercase text-emerald-700">Total Credited (cards diye)</div>
                <div className="font-bold text-emerald-900">{fmt(ledger.balance?.credit || 0)}</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded p-2">
                <div className="text-[10px] uppercase text-red-700">Total Paid Out (payment hua)</div>
                <div className="font-bold text-red-900">{fmt(ledger.balance?.debit || 0)}</div>
              </div>
              <div className="bg-fuchsia-50 border border-fuchsia-200 rounded p-2 col-span-2">
                <div className="text-[10px] uppercase text-fuchsia-700 flex items-center gap-1"><Wallet size={12} /> Balance (owed)</div>
                <div className="font-bold text-fuchsia-900 text-lg">{fmt(ledger.balance?.balance || 0)}</div>
              </div>
            </div>
          </div>

          {/* Bank Accounts */}
          <div className="bg-white border rounded-xl p-4">
            <div className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
              <Buildings size={16} className="text-blue-600" /> Payment Receive Accounts ({accounts.length})
            </div>
            {accounts.length === 0 ? <div className="text-xs text-gray-500">Koi account configured nahi</div>
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Holder</th>
                      <th className="px-2 py-1.5 text-left">A/C Number</th>
                      <th className="px-2 py-1.5 text-left">IFSC</th>
                      <th className="px-2 py-1.5 text-left">Bank</th>
                      <th className="px-2 py-1.5 text-left">UPI</th>
                      <th className="px-2 py-1.5 text-center">Primary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((a) => (
                      <tr key={a.id} className="border-t">
                        <td className="px-2 py-1.5 font-semibold">{a.account_holder}</td>
                        <td className="px-2 py-1.5 font-mono">{a.account_number || '—'}</td>
                        <td className="px-2 py-1.5 font-mono">{a.ifsc || '—'}</td>
                        <td className="px-2 py-1.5">{a.bank_name || '—'}</td>
                        <td className="px-2 py-1.5 font-mono text-indigo-700">{a.upi_id || '—'}</td>
                        <td className="px-2 py-1.5 text-center">{a.is_primary && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1 rounded">PRIMARY</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Ledger */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b font-semibold text-gray-900 flex items-center gap-2">
              <Wallet size={16} /> Ledger ({ledger.entries?.length || 0} entries)
            </div>
            {detailLoading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
            : (ledger.entries?.length || 0) === 0 ? <div className="p-8 text-center text-sm text-gray-500">No entries</div>
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Category</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-left">Payment Mode / Details</th>
                      <th className="px-3 py-2 text-left">Remarks</th>
                      <th className="px-3 py-2 text-left">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.entries.map((e, i) => (
                      <tr key={e.id || `${e.created_at}-${i}`} className="border-t">
                        <td className="px-3 py-2 text-xs">{fmtDate(e.created_at)}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${e.type === 'credit' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {e.type === 'credit' ? 'CR (+)' : 'DR (−)'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">{e.category}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${e.type === 'credit' ? 'text-emerald-700' : 'text-red-700'}`}>
                          {e.type === 'credit' ? '+' : '−'} {fmt(e.amount)}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {e.payment_mode_label && <div className="font-semibold">{e.payment_mode_label}</div>}
                          {e.account_holder && <div className="text-gray-500">{e.account_holder} • {e.account_number} • {e.ifsc}</div>}
                          {e.upi_txn_id && <div className="text-gray-500">UPI: {e.upi_txn_id}</div>}
                          {e.receipt_number && <div className="text-gray-500">Receipt: {e.receipt_number}</div>}
                          {e.txn_reference && <div className="text-gray-500">Ref: {e.txn_reference}</div>}
                          {e.ticket_no && <div className="text-indigo-600 font-mono text-[10px]">{e.ticket_no}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 max-w-[260px] truncate">{e.remarks || '—'}</td>
                        <td className="px-3 py-2 text-xs">{e.created_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {meta.can_manage_access && <AccessDialog open={showAccess} onClose={() => setShowAccess(false)} />}
    </div>
  );
}
